import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { Panel } from '~/components/v2/components/shared/panel/Panel';
import {
  ScrollElementContextConsumer,
  ScrollElementContextProvider,
} from '~/components/v2/components/shared/scroll/ScrollElementContextProvider';
import { WorkspaceHeaderInformation } from '~/components/v2/components/workspace/workspace-header/WorkspaceHeaderInformation';
import {
  WORKSPACE_SIDEBAR_HOTKEYS_SCOPE,
  WorkspaceSidebarToken,
} from '~/components/v2/components/workspace/workspace-sidebar/getWorkspaceSidebarEventToken';
import { WorkspaceLibraryUpsellCard } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceLibraryUpsellCard';
import { WorkspaceSidebarContainer } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarContainer';
import { WorkspaceSidebarFavoriteBoards } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarFavoriteBoards';
import { WorkspaceSidebarFooter } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarFooter';
import { WorkspaceSidebarLibraries } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarLibraries';
import { WorkspaceSidebarNavigation } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarNavigation';
import { WorkspaceSidebarRecentCanvases } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarRecentCanvases';
import { WorkspaceSidebarSection } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSection';
import { WorkspaceSidebarSectionSpacer } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionSpacer';
import { WorkspaceSidebarSectionStoreContextProvider } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { WorkspaceSidebarTeamInviteNudge } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarTeamInviteNudge';
import { WorkspaceSidebarWorkspaceBoards } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarWorkspaceBoards';
import { useWorkspaceNavPushMode } from '~/components/v2/hooks/useWorkspaceNavPushMode';
import { useWorkspaceNotificationsStore } from '~/components/v2/store/features/workspace/useWorkspaceNotificationsStore';
import { useWorkspaceRightPanelOpen } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelOpen';
import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { useWorkspaceSidebarPushStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarPushStore';
import { useWorkspaceSidebarStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarStore';
import { ZustandStorageKeys } from '~/constants/zustandStorageKeys';
import { usePrivateAddOnFeatures } from '~/hooks/usePrivateAddOnFeatures';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export const WorkspaceSidebar = memo(() => {
  const open = useWorkspaceSidebarStore((state) => state.open);
  const toggleOpen = useWorkspaceSidebarStore((state) => state.toggleOpen);
  const notificationsOpen = useWorkspaceNotificationsStore((state) => state.open);
  const setNotificationsOpen = useWorkspaceNotificationsStore((state) => state.setOpen);
  const { libraries } = usePrivateAddOnFeatures();
  const { isWorkspaceNavPushMode } = useWorkspaceNavPushMode();
  const showAsPush = isWorkspaceNavPushMode || !open;
  const navRef = useRef<HTMLElement>(null);
  const [isPushFocused, setIsPushFocused] = useState(false);

  const sidebarPushOpen = useWorkspaceSidebarPushStore((s) => s.isOpen);
  const pushLockCount = useWorkspaceSidebarPushStore((s) => s.lockCount);
  const isSuppressHoverOpen = useWorkspaceSidebarPushStore((s) => s.isSuppressHoverOpen);
  const openPush = useWorkspaceSidebarPushStore((s) => s.openPush);
  const closePush = useWorkspaceSidebarPushStore((s) => s.closePush);
  const lockPush = useWorkspaceSidebarPushStore((s) => s.lockPush);
  const unlockPush = useWorkspaceSidebarPushStore((s) => s.unlockPush);
  const unsuppressHoverOpen = useWorkspaceSidebarPushStore((s) => s.unsuppressHoverOpen);

  useEffect(() => {
    if (notificationsOpen) {
      lockPush(WorkspaceSidebarToken.Notifications);
    } else {
      unlockPush(WorkspaceSidebarToken.Notifications);
    }
  }, [notificationsOpen, lockPush, unlockPush]);

  useEffect(() => {
    if (!showAsPush) {
      return;
    }

    if (sidebarPushOpen) {
      setIsPushFocused(true);
    } else {
      setIsPushFocused(false);
      const navElement = navRef.current;
      const activeElement = document.activeElement;

      if (navElement && activeElement instanceof HTMLElement && navElement.contains(activeElement)) {
        activeElement.blur();
      }
    }
  }, [sidebarPushOpen, showAsPush]);

  const { generativeCanvasEnabled } = useTemporaryFeatureFlags();
  const handlePushFocus = useCallback(() => setIsPushFocused(true), []);
  const handlePushBlur = useCallback(() => {
    if (notificationsOpen || pushLockCount > 0) {
      return;
    }
    setIsPushFocused(false);
  }, [notificationsOpen, pushLockCount]);
  const handleNotificationsHover = useCallback(() => {
    if (notificationsOpen) {
      setNotificationsOpen(false);
    }

    if (showAsPush) {
      openPush();
    }
  }, [showAsPush, notificationsOpen, setNotificationsOpen, openPush]);

  const handlePushPointerLeave = useCallback(() => {
    if (showAsPush) {
      closePush();
      unsuppressHoverOpen(WorkspaceSidebarToken.Hamburger);
    }
  }, [showAsPush, closePush, unsuppressHoverOpen]);

  const handleHoverZoneLeave = useCallback(() => {
    unsuppressHoverOpen(WorkspaceSidebarToken.CloseAfterNotifications);
  }, [unsuppressHoverOpen]);

  useEffect(() => {
    if (showAsPush && notificationsOpen) {
      setIsPushFocused(true);
    }
  }, [showAsPush, notificationsOpen]);

  useHotkeys(
    'meta+\\,ctrl+\\,w',
    () => {
      if (isWorkspaceNavPushMode) {
        return;
      }

      toggleOpen();
    },
    {
      preventDefault: true,
      scopes: [WORKSPACE_SIDEBAR_HOTKEYS_SCOPE],
    },
    [isWorkspaceNavPushMode, toggleOpen],
  );

  const rightPanelOpen = useWorkspaceRightPanelOpen();
  const isFullscreen = useWorkspaceRightPanelStore((state) => state.isFullscreen);

  if (isFullscreen) {
    return null;
  }

  const sidebarNavContent = (
    <>
      <ScrollElementContextConsumer>
        {({ setScrollElement }) => (
          <div className="flex grow flex-col overflow-y-auto px-3" ref={setScrollElement}>
            <WorkspaceLibraryUpsellCard />
            <WorkspaceSidebarSection>
              <WorkspaceSidebarNavigation />
            </WorkspaceSidebarSection>

            <WorkspaceSidebarSectionSpacer />
            {generativeCanvasEnabled && (
              <WorkspaceSidebarSectionStoreContextProvider
                storageKey={ZustandStorageKeys.WorkspaceSidebarSectionRecentCanvases}
              >
                <WorkspaceSidebarRecentCanvases />
              </WorkspaceSidebarSectionStoreContextProvider>
            )}

            <WorkspaceSidebarSectionStoreContextProvider
              storageKey={ZustandStorageKeys.WorkspaceSidebarSectionFavoriteBoards}
            >
              <WorkspaceSidebarFavoriteBoards />
            </WorkspaceSidebarSectionStoreContextProvider>

            <WorkspaceSidebarSectionSpacer />

            {libraries ? (
              <WorkspaceSidebarSectionStoreContextProvider
                storageKey={ZustandStorageKeys.WorkspaceSidebarSectionLibraries}
              >
                <WorkspaceSidebarLibraries />
              </WorkspaceSidebarSectionStoreContextProvider>
            ) : (
              <WorkspaceSidebarSectionStoreContextProvider
                storageKey={ZustandStorageKeys.WorkspaceSidebarSectionWorkspaceBoards}
              >
                <WorkspaceSidebarWorkspaceBoards />
              </WorkspaceSidebarSectionStoreContextProvider>
            )}

            <WorkspaceSidebarTeamInviteNudge />
          </div>
        )}
      </ScrollElementContextConsumer>

      <WorkspaceSidebarFooter />
    </>
  );

  return (
    <ScrollElementContextProvider>
      {showAsPush && (
        <WorkspaceSidebarContainer
          isPushMode
          isPushFocused={isPushFocused || pushLockCount > 0}
          suppressHoverOpen={isSuppressHoverOpen}
          onPushFocus={handlePushFocus}
          onPushBlur={handlePushBlur}
          onNotificationsHover={handleNotificationsHover}
          onHoverZoneLeave={handleHoverZoneLeave}
          onPushPointerLeave={handlePushPointerLeave}
        >
          <nav
            aria-label="Workspace navigation"
            className="relative flex h-full min-h-0 w-full grow flex-col"
            data-workspace-sidebar-nav
            ref={navRef}
          >
            {rightPanelOpen && (
              <div className="flex h-16 shrink-0 items-center">
                <WorkspaceHeaderInformation />
              </div>
            )}
            <div className="flex min-h-0 grow flex-col">{sidebarNavContent}</div>
          </nav>
        </WorkspaceSidebarContainer>
      )}
      {!isWorkspaceNavPushMode && (
        <Panel open={open}>
          <WorkspaceSidebarContainer
            isPushFocused={isPushFocused || pushLockCount > 0}
            suppressHoverOpen={isSuppressHoverOpen}
            onPushFocus={handlePushFocus}
            onPushBlur={handlePushBlur}
            onNotificationsHover={handleNotificationsHover}
            onPushPointerLeave={handlePushPointerLeave}
          >
            <nav
              aria-label="Workspace navigation"
              className="relative flex h-full min-h-0 w-full grow flex-col"
              data-workspace-sidebar-nav
              ref={navRef}
            >
              {rightPanelOpen && (
                <div className="flex h-16 shrink-0 items-center">
                  <WorkspaceHeaderInformation />
                </div>
              )}
              <div className="flex min-h-0 grow flex-col">{sidebarNavContent}</div>
            </nav>
          </WorkspaceSidebarContainer>
        </Panel>
      )}
    </ScrollElementContextProvider>
  );
});

WorkspaceSidebar.displayName = 'WorkspaceSidebar';
